import WalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BusinessIcon from '@mui/icons-material/Business';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import UploadIcon from '@mui/icons-material/Upload';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Loading } from '@/core/components';
import { useAuth } from '@/core/hooks/useAuth';
import { useDocumentTitle } from '@/core/hooks/useDocumentTitle';
import { isOperator } from '@/core/services/auth';

/** 運営管理画面用グラデーション（黄→赤） */
const operatorGradient = 'linear-gradient(to right, #ffff00, #ff0000)';

const menuItems = [
  { path: 'clients', label: '主催者管理', icon: <BusinessIcon /> },
  { path: 'races', label: '大会管理', icon: <FolderOpenIcon /> },
  { path: 'import', label: 'インポート', icon: <UploadIcon /> },
  { path: 'wallets', label: 'ウォレット管理', icon: <WalletIcon /> },
];

export function OperatorLayout() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOperatorUser, setIsOperatorUser] = useState<boolean | null>(null);
  const [checkingRole, setCheckingRole] = useState(true);

  // 現在のセクションを取得（サブページでも親メニューをハイライト）
  const pathSegments = location.pathname.split('/').filter(Boolean);
  // /admin/clients/new → ['admin', 'clients', 'new'] → 'clients'
  const currentSection = pathSegments[1] || '';
  const currentMenuItem = menuItems.find((item) => item.path === currentSection);
  const pageName = currentMenuItem?.label || '管理画面';

  // タイトル設定
  useDocumentTitle(`${pageName} | RunPort NFT 運営管理画面`);

  const checkOperatorRole = useCallback(async () => {
    if (!user) {
      setIsOperatorUser(false);
      setCheckingRole(false);
      return;
    }

    const operatorStatus = await isOperator(user.uid);
    setIsOperatorUser(operatorStatus);
    setCheckingRole(false);
  }, [user]);

  useEffect(() => {
    if (!authLoading) {
      checkOperatorRole();
    }
  }, [authLoading, checkOperatorRole]);

  if (authLoading || checkingRole) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Loading />
      </Container>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  if (!isOperatorUser) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">運営者権限がありません</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* ヘッダー */}
      <Box
        component="header"
        sx={{
          background: operatorGradient,
          py: 2,
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 700,
            color: '#1D1B20',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          RunPort NFT
          <Typography component="span" variant="h6" sx={{ fontWeight: 400 }}>
            運営管理画面
          </Typography>
        </Typography>
      </Box>

      {/* メインコンテンツ */}
      <Container maxWidth="xl" sx={{ py: 4, flexGrow: 1 }}>
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* サイドバー */}
          <Paper
            sx={{
              width: 220,
              flexShrink: 0,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <List disablePadding>
              {menuItems.map((item) => (
                <ListItemButton
                  key={item.path}
                  selected={currentSection === item.path}
                  onClick={() => navigate(`/admin/${item.path}`)}
                  sx={{
                    '&.Mui-selected': {
                      background: operatorGradient,
                      color: '#1D1B20',
                      '& .MuiListItemIcon-root': {
                        color: '#1D1B20',
                      },
                      '&:hover': {
                        background: operatorGradient,
                        filter: 'brightness(0.95)',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
            </List>
          </Paper>

          {/* コンテンツエリア */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Outlet />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
